import { outstandingRefund, refundCardUnreachable } from '../lib/reservationRefund'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, CalendarDays, ChevronDown, ExternalLink, MapPin, Phone, Share2, Store, Ticket, UtensilsCrossed, Users, X } from 'lucide-react'
import Header from '../components/Header'
import { showToast } from '../components/Toast'
import { noShowOutcome } from '../lib/noShowOutcome'
import { useI18n } from '../i18n/useI18n'
import { DEFAULT_MAX_PARTY_SIZE } from '../lib/partySize'
import { useStore, type Reservation } from '../store/useStore'
import { useStoreNames } from '../hooks/useStoreNames'
import { api, type ExperienceTicket } from '../services/api'
import { useMyReservations } from '../hooks/useMyReservations'
import { useMyExperienceTickets } from '../hooks/useMyExperienceTickets'
import {
  CANCEL_DEADLINE_HOURS,
  CANCELLATION_HANDLING_FEE_PCT,
  DEPOSIT_FULL_REFUND_HOURS,
  isDepositRefundable,
  prepaidRefundBreakdownByHours,
} from './StoreDetail/reservationPolicy'
import ReservationShareSheet from '../components/ReservationShareSheet'
import DishListPanel from '../components/DishListPanel'
import RefundVoucherCard from '../components/RefundVoucherCard'
import ChangePartySizeModal from '../components/ChangePartySizeModal'
import ChangeDatetimeModal from '../components/ChangeDatetimeModal'
import ChangeCourseModal from '../components/ChangeCourseModal'
import { useCourse } from '../hooks/useCourses'
import { formatSlotDateTime } from '../utils/experienceSlot'
import { isPointsMethod } from '../lib/paymentMethods'

type SubTab = 'active' | 'history'

// #1291: 体験チケットグループ (カード1枚)。#1292: timed は商品×枠でまとめる
// (回ごとに1カード)、free_entry は商品でまとめる (slotId が null なので自動的に商品単位)。
type TicketGroup = {
  key: string
  storeId: string
  productId?: string
  productName: string
  productNameEn: string
  priceJpy: number
  slotDate?: string | null
  slotStartTime?: string | null
  tickets: ExperienceTicket[]
}

const CANCEL_REASONS = [
  'scheduleChange',
  'emergency',
  'travelChange',
  'wrongReservation',
  'other',
] as const

export default function MyReservations() {
  const navigate = useNavigate()
  const { t, locale } = useI18n()
  const { stores } = useStore()

  // 予約日時までの残り時間 (h)。段階返金の判定に使う。JST 固定で組む —
  // 予約の date/time は店の営業時間として JST で入力されている。
  const hoursUntilReservation = (r: Reservation): number => {
    const target = new Date(`${r.date}T${r.time}:00+09:00`).getTime()
    return (target - Date.now()) / 3600_000
  }

  // #1374 キャンセル料が「かかるのか / いくらか」を、押す前に出す。
  //
  // 承認待ちの間は無料。承認前は店がまだ受けていないので、客に負担させる理由が無い
  // (#1374 で与信化すれば決済手数料も発生しない)。迷っているなら早く決めた方が
  // 得だと分かるようにする。
  //
  // 承認後は規定の段階返金。率だけでは判断できないので**返金額を金額で**出す。
  const cancelFeeNote = (r: Reservation): string => {
    // 変更の再承認待ち (pendingChange) は「承認済み・決済確定済み」の予約なので、
    // 承認待ちの無料扱いにしない (文言と実際の返金が食い違っていた原因)
    if (r.status === 'pending' && !r.pendingChange) return t('myReservations.cancelFree')
    // #2018 承認待ちの増員は総額に足されているが金はまだ預かっていない。規定の対象は
    // 実際に預かっている額 (backend の計算と同じ)
    const gross = r.totalAmount ?? 0
    const total = typeof r.paidAmount === 'number' && r.paidAmount > 0 ? Math.min(gross, r.paidAmount) : gross
    if (!r.paymentIntentId || total <= 0) return t('myReservations.cancelNoPayment')
    const hoursUntil = hoursUntilReservation(r)
    // 金額だけでなく「なぜその額か」(何日前の段階か / 手続き料がいくらか) を添える。
    // 全額返金の期間でも手続き料 6% が引かれるので、理由が無いと減額に見える。
    if (r.paymentTiming === 'deposit') {
      const deposit = r.depositAmount ?? 0
      const days = DEPOSIT_FULL_REFUND_HOURS / 24
      return isDepositRefundable(hoursUntil)
        ? t('myReservations.cancelDepositRefundReason', { amount: deposit.toLocaleString(), days })
        : t('myReservations.cancelDepositForfeitReason', { amount: deposit.toLocaleString(), days })
    }
    const b = prepaidRefundBreakdownByHours(total, hoursUntil)
    if (b.refund <= 0) return t('myReservations.cancelNoRefundReason', { days: b.withinDays ?? 1 })
    const params = {
      amount: b.refund.toLocaleString(),
      gross: b.gross.toLocaleString(),
      fee: b.fee.toLocaleString(),
      feePct: CANCELLATION_HANDLING_FEE_PCT,
      pct: b.tierPct,
      days: b.withinDays ?? 0,
    }
    return b.tierPct === 100
      ? t('myReservations.cancelRefundFullFee', params)
      : t('myReservations.cancelRefundTierFee', params)
  }

  // #1373 キャンセル時に「いくら返ってきたのか」を履歴に出す。
  // お店都合 (store / system) は手続き料を引かず全額、自己都合は規定の段階返金。
  // 決済していない予約 (当日払い) は返金の話自体が無いので何も出さない。
  const refundNote = (r: Reservation): string | null => {
    if (!r.paymentIntentId) return null
    const byStore = r.cancelledBy === 'store' || r.cancelledBy === 'system'
    // #2018 承認待ちの増員は総額に足されているが金はまだ預かっていない。返金の話は
    // 実際に預かった額 (paidAmount) を上限にする (backend の規定の基準と同じ)
    const gross = r.paymentTiming === 'deposit' ? (r.depositAmount ?? 0) : (r.totalAmount ?? 0)
    const paid = typeof r.paidAmount === 'number' && r.paidAmount > 0 ? Math.min(gross, r.paidAmount) : gross
    if (paid <= 0) return null
    // 課金が無い取消 (承認前の取消 = 与信の解放だけ / 確定できなかった予約) には
    // 返金の話をしない。「返金を確認しています」と出すと、起きていない返金を待たせてしまう
    if (!['paid', 'partially_refunded', 'refunded'].includes(r.paymentStatus ?? '')) {
      return null
    }
    // #2476 申し立て (チャージバック) が来ている: カード会社から戻る。お店に行かせない
    if (r.refundDisputed) {
      return t('refundOnline.disputed')
    }
    // #2226 カードに返せなかった (解約・期限切れなど)。お店へ連絡してもらい、お店が別の方法で返す
    if (refundCardUnreachable(r)) {
      return t('myReservations.refundCardUnreachable', { amount: outstandingRefund(r).toLocaleString() })
    }
    if (r.refundStatus === 'alternate' && r.refundAlternate) {
      return t('myReservations.refundAlternateDone', {
        amount: r.refundAlternate.amount.toLocaleString(),
        method: t(`myReservations.refundAlternateMethod.${r.refundAlternate.method}`),
      })
    }
    if (byStore) {
      return t('myReservations.refundStoreCancel', { amount: paid.toLocaleString() })
    }
    // #1504 「規定に基づき返金しました（手続き料を差し引き）」は、手付金没収 (返金ゼロ) や
    // 手付金の全額返金 (手続き料なし) でも同じ文が出て実態と食い違っていた。
    // 実返金額 (refundedAmount = サーバが Stripe の結果から書く真実) で出し分ける。
    // #2018 返金が走っていない / 失敗 = 額が無い。ここで「返金しました」と言わない
    // (返っていないのに返ったと読める)。額が確定したら下の分岐で額を出す
    if (typeof r.refundedAmount !== 'number' || r.refundStatus === 'failed') {
      return t('myReservations.refundCustomerCancel')
    }
    const refunded = r.refundedAmount
    if (refunded <= 0) {
      return t('myReservations.refundCustomerCancelNone', { amount: paid.toLocaleString() })
    }
    if (r.paymentTiming === 'deposit' && refunded >= paid) {
      return t('myReservations.refundCustomerCancelDepositFull', { amount: refunded.toLocaleString() })
    }
    return t('myReservations.refundCustomerCancelAmount', {
      amount: refunded.toLocaleString(),
      kept: Math.max(0, paid - refunded).toLocaleString(),
    })
  }

  const { active, history, loading } = useMyReservations()
  // #1283: 体験チケットも同じ進行中/履歴タブに統合表示 (案A)
  const { active: ticketsActive, history: ticketsHistory } = useMyExperienceTickets()
  const [tab, setTab] = useState<SubTab>('active')
  const [cancelling, setCancelling] = useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = useState<Reservation | null>(null)
  const [cancelReason, setCancelReason] = useState<string>('scheduleChange')
  // #746: 各 reservation card から share sheet を起動 — 1 sheet 共有で state 1 個。
  const [shareTarget, setShareTarget] = useState<Reservation | null>(null)
  // #1291: 体験チケットの共有 sheet (予約と同じ ReservationShareSheet を流用)。
  // 共有先は予約内容の公開ページ (/e/:shareCode) — 店舗ページではなく「何を予約したか」。
  const [ticketShareTarget, setTicketShareTarget] = useState<{ group: TicketGroup; shareCode: string } | null>(null)
  const [resolvingTicketShare, setResolvingTicketShare] = useState<string | null>(null)
  // #746: lazy backfill 中の reservation id (= 旧予約をクリックしてから sheet 開くまでの遅延中)
  const [resolvingShortCode, setResolvingShortCode] = useState<string | null>(null)
  // #745 Phase 2.1/2.2/2.3: 変更モーダルの target reservation (3 種類のいずれか open)
  const [partySizeTarget, setPartySizeTarget] = useState<Reservation | null>(null)
  const [datetimeTarget, setDatetimeTarget] = useState<Reservation | null>(null)
  const [courseTarget, setCourseTarget] = useState<Reservation | null>(null)
  // #1644 「コース内容」を開いている予約 id (品目スナップショットの開閉)
  const [contentsOpenId, setContentsOpenId] = useState<string | null>(null)
  // 人数変更モーダル open 中だけ course 情報を fetch (= 範囲 + 単価 取得)
  const { course: targetCourse } = useCourse(
    partySizeTarget?.storeId ?? null,
    partySizeTarget?.courseId ?? null,
  )

  const storeById = (storeId: string) => stores.find(s => s.id === storeId)
  // **名前は自分で揃える。** zustand の `stores` は Home でしか埋まらないので、
  // HOME を経由せずに来ると店舗名が ID のまま出ていた。
  const storeNameById = useStoreNames([
    ...active.map(r => r.storeId),
    ...history.map(r => r.storeId),
    ...ticketsActive.map(t => t.storeId),
    ...ticketsHistory.map(t => t.storeId),
  ])

  // #1297: 手付金コースで手付金の決済が完了しているか。承認待ち等でまだ
  // 支払っていない段階で「支払い済み」と出すと嘘になるので paymentStatus を見る。
  // partially_refunded は人数変更の部分返金後 (= 手付金自体は支払済み) の状態。
  const isDepositPaid = (r: Reservation) =>
    r.paymentTiming === 'deposit' &&
    typeof r.depositAmount === 'number' && r.depositAmount > 0 &&
    (r.paymentStatus === 'paid' || r.paymentStatus === 'partially_refunded')

  // Is this approved reservation still cancellable by customer (within platform-wide deadline)?
  const canCancelApproved = (r: Reservation) => {
    const reserveAt = new Date(`${r.date}T${r.time}:00+09:00`).getTime()
    return (reserveAt - Date.now()) / 3600000 >= CANCEL_DEADLINE_HOURS
  }

  const openCancelModal = (r: Reservation) => {
    // 変更の再承認待ちも承認済みと同じ期限 (サーバ側も同じ判定)
    if ((r.status === 'approved' || r.pendingChange) && !canCancelApproved(r)) {
      showToast({ type: 'error', message: t('myReservations.cancelDeadlinePassed') })
      return
    }
    setCancelReason('scheduleChange')
    setCancelTarget(r)
  }

  // #746: shortCode が無い旧予約は lazy backfill してから処理 (= share sheet 起動 / 詳細遷移)
  const withShortCode = async (
    r: Reservation,
    cb: (shortCode: string) => void,
  ) => {
    if (r.shortCode) {
      cb(r.shortCode)
      return
    }
    if (resolvingShortCode === r.id) return
    setResolvingShortCode(r.id)
    try {
      const { shortCode } = await api.ensureReservationShortCode(r.id)
      cb(shortCode)
    } catch (e) {
      console.error('[MyReservations] ensureShortCode failed:', e)
      showToast({ type: 'error', message: t('myReservations.shareFailed') })
    } finally {
      setResolvingShortCode(null)
    }
  }

  const openShareSheet = (r: Reservation) =>
    withShortCode(r, (shortCode) => setShareTarget({ ...r, shortCode }))

  const goToDetail = (r: Reservation) =>
    withShortCode(r, (shortCode) => navigate(`/rsv/${shortCode}`))

  // #1291: 体験チケットの共有。予約と同じく shareCode を確保して共有ページ URL を作る。
  // shareCode は発行時に採番済みが基本、未採番の旧チケットは ensure で後追い採番。
  const openTicketShare = async (g: TicketGroup) => {
    const paid = g.tickets.find(ti => ti.paymentStatus === 'paid')
    if (!paid) {
      showToast({ type: 'error', message: t('myReservations.shareFailed') })
      return
    }
    if (paid.shareCode) {
      setTicketShareTarget({ group: g, shareCode: paid.shareCode })
      return
    }
    if (resolvingTicketShare === g.key) return
    setResolvingTicketShare(g.key)
    try {
      const { shareCode } = await api.ensureExperienceShareCode(paid.id)
      setTicketShareTarget({ group: g, shareCode })
    } catch (e) {
      console.error('[MyReservations] ensureExperienceShareCode failed:', e)
      showToast({ type: 'error', message: t('myReservations.shareFailed') })
    } finally {
      setResolvingTicketShare(null)
    }
  }

  const submitCancel = async () => {
    if (!cancelTarget || cancelling) return
    setCancelling(cancelTarget.id)
    try {
      await api.updateReservationStatus(cancelTarget.id, 'cancelled', { reasonCode: cancelReason })
      showToast({ type: 'success', message: t('myReservations.cancelled') })
      setCancelTarget(null)
      // onSnapshot reflects the change automatically — no manual refresh needed
    } catch (e) {
      console.error('[MyReservations] cancel failed:', e)
    } finally {
      setCancelling(null)
    }
  }

  const filtered = tab === 'active' ? active : history
  const ticketsFiltered = tab === 'active' ? ticketsActive : ticketsHistory
  // #1291: 同一商品のチケットは1カードにまとめ、枚数 + チケットボタンリストで見せる。
  // 体験チケットは購入後変更不可なので飲食店予約とは別カード (2パターン) にする。
  const ticketGroups: TicketGroup[] = Object.values(
    ticketsFiltered.reduce((acc: Record<string, TicketGroup>, ti) => {
      // timed は枠 (日時) ごとに別カード。free_entry は日時が無いので商品単位。
      const key = [ti.productId || ti.productName || ti.id, ti.slotDate || '', ti.slotStartTime || ''].join('|')
      if (!acc[key]) acc[key] = {
        key, storeId: ti.storeId, productId: ti.productId,
        productName: ti.productName, productNameEn: ti.productNameEn, priceJpy: ti.priceJpy,
        slotDate: ti.slotDate, slotStartTime: ti.slotStartTime, tickets: [],
      }
      acc[key].tickets.push(ti)
      return acc
    }, {}),
  )

  // #1283: 体験チケットのステータスバッジ (experience.* キーを流用)
  const ticketBadge = (ti: ExperienceTicket) => {
    const [label, cls] =
      ti.refundStatus === 'failed'
        // #2298 返金がカード会社で通らなかった (詳しい画面に引換券)
        ? [t('experience.statusRefundFailed'), 'bg-yokoso-red/10 text-yokoso-red']
        : ti.refundStatus === 'alternate'
          ? [t('experience.statusRefundAlternate'), 'bg-gray-200 text-text-secondary']
          : ti.paymentStatus === 'pending'
        ? [t('experience.statusPending'), 'bg-gold/15 text-gold-dark']
        : ti.paymentStatus === 'failed'
          ? [t('experience.statusFailed'), 'bg-yokoso-red/10 text-yokoso-red']
          : ti.status === 'redeemed'
            ? [t('experience.statusUsed'), 'bg-gray-200 text-text-secondary']
            : [t('experience.statusValid'), 'bg-matcha/15 text-matcha']
    return (
      <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-body font-medium ${cls}`}>
        {label}
      </span>
    )
  }

  // #1371 no_show のラベルを実態で出し分ける。決済手段が無い予約でも
  // 「キャンセル料請求済」と出ており、**請求されていない客に請求されたと
  // 誤解させていた**（問い合わせの元になる）。
  const statusBadge = (r: Reservation) => {
    const status = r.status
    const colorMap: Record<Reservation['status'], string> = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-matcha/20 text-matcha',
      rejected: 'bg-gray-200 text-text-secondary',
      cancelled: 'bg-gray-200 text-text-secondary',
      expired: 'bg-orange-100 text-orange-700',
      // #738 B-1: 来店マーク後の終端 status (顧客側 my-reservations 履歴に表示)
      visited: 'bg-matcha/20 text-matcha',
      no_show: 'bg-yokoso-red/20 text-yokoso-red',
    }
    return (
      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-body font-semibold ${colorMap[status]}`}>
        {status === 'no_show'
          ? t(`myReservations.noShow.${noShowOutcome(r).kind}`)
          : t(`myReservations.status.${status}`)}
      </span>
    )
  }

  return (
    <div className="min-h-screen bg-warm-white safe-top pb-20">
      <Header title={t('myReservations.title')} onBack={() => navigate(-1)} />

      {/* Sub tabs */}
      <div className="flex border-b border-gray-100">
        {(['active', 'history'] as SubTab[]).map(k => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`flex-1 py-3 text-sm font-body transition-colors ${
              tab === k
                ? 'text-yokoso-red font-semibold border-b-2 border-yokoso-red'
                : 'text-text-secondary'
            }`}
          >
            {t(`myReservations.tab${k === 'active' ? 'Active' : 'History'}`)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-4 pt-4 space-y-3">
        {loading ? (
          <p className="text-center py-16 text-text-tertiary font-body text-sm">...</p>
        ) : filtered.length === 0 && ticketsFiltered.length === 0 ? (
          <div className="text-center py-16">
            <CalendarDays size={40} className="mx-auto text-text-tertiary mb-2" />
            <p className="text-text-tertiary font-body text-sm">{t('myReservations.empty')}</p>
          </div>
        ) : (
          <>
          {filtered.map(r => {
            const store = storeById(r.storeId)
            // #1297: 手付金支払済みは「合計 (単価×人数) / 支払い済み / 残額」の
            // 内訳 3 行で表示。単価は割り切れる場合のみ表示 (人数変更後の端数対策)。
            const depositPaid = isDepositPaid(r) &&
              typeof r.totalAmount === 'number' && r.totalAmount > 0
            const unitOk = depositPaid && r.partySize > 0 &&
              r.totalAmount! % r.partySize === 0 && r.depositAmount! % r.partySize === 0
            const remainder = depositPaid ? r.totalAmount! - r.depositAmount! : 0
            return (
            <div key={r.id} className="bg-white rounded-2xl shadow-card p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="font-jp font-semibold text-text-primary truncate flex-1">
                  {storeNameById(r.storeId)}
                </p>
                {statusBadge(r)}
              </div>

              <p className="font-body text-sm text-text-primary">
                {r.date} {r.time} · {r.partySize}名
              </p>

              {/* #746: コース / 合計金額 (ある場合だけ表示)
                  #1297: 手付金支払済みの予約はコース全額の一行表示でなく
                  「合計金額 (単価×人数) / 支払い済み (手付金) / 残額」の内訳で
                  表示する (全額だけ見せると二重払い/未払いの誤認を招く) */}
              {r.courseName && (
                <p className="font-body text-xs text-text-secondary mt-1.5">
                  {r.courseName}
                  {!depositPaid && typeof r.totalAmount === 'number' && r.totalAmount > 0 && (
                    <> · {t('myReservations.totalAmount', { amount: r.totalAmount.toLocaleString() })}</>
                  )}
                </p>
              )}
              {/* #1644 選択した飲み放題プラン (予約時スナップショット) */}
              {r.drinkPlanName && (
                <p className="font-body text-xs text-text-secondary mt-0.5">
                  🍻 {r.drinkPlanName}
                  {typeof r.drinkPlanPriceYen === 'number' && r.drinkPlanPriceYen > 0 && (
                    <>
                      {' '}
                      {t('myReservations.drinkPlanBreakdown', {
                        unit: r.drinkPlanPriceYen.toLocaleString(),
                        count: String(r.partySize),
                      })}
                    </>
                  )}
                </p>
              )}
              {/* #1644 コース内容 (予約時の品目スナップショット)。「何が出てくるか」を
                  予約後にも確認できるようにする — 以前は予約前のコース詳細でしか見えなかった */}
              {r.courseDishList && (
                <div className="mt-1">
                  <button
                    onClick={() => setContentsOpenId(contentsOpenId === r.id ? null : r.id)}
                    className="tap-effect inline-flex items-center gap-0.5 font-body text-xs font-medium text-navy"
                  >
                    {t('myReservations.courseContents')}
                    <ChevronDown
                      size={13}
                      className={`transition-transform ${contentsOpenId === r.id ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {contentsOpenId === r.id && (
                    <div className="mt-1">
                      <DishListPanel dishList={r.courseDishList} size="xs" />
                    </div>
                  )}
                </div>
              )}
              {depositPaid && (
                <div className="mt-1 space-y-0.5 font-body text-xs text-text-secondary">
                  <p>{unitOk
                    ? t('myReservations.totalWithBreakdown', {
                        unit: (r.totalAmount! / r.partySize).toLocaleString(),
                        count: String(r.partySize),
                        total: r.totalAmount!.toLocaleString(),
                      })
                    : t('myReservations.totalLabel', { total: r.totalAmount!.toLocaleString() })}
                  </p>
                  <p>{unitOk
                    ? t('myReservations.depositPaidWithBreakdown', {
                        unit: (r.depositAmount! / r.partySize).toLocaleString(),
                        count: String(r.partySize),
                        amount: r.depositAmount!.toLocaleString(),
                      })
                    : t('myReservations.depositPaidLabel', { amount: r.depositAmount!.toLocaleString() })}
                  </p>
                  {remainder > 0 && (
                    <p className="text-text-primary font-medium">
                      {t('myReservations.remainderLabel', { amount: remainder.toLocaleString() })}
                    </p>
                  )}
                </div>
              )}

              {/* #746: 店舗住所 (= 共有された予約と同じく場所を確認できる)
                  #1043: タップで Google Map を開けるようリンク化 (StoreDetail/InfoTab.tsx の
                  「地図で開く」と同じ URL 生成ロジックを再利用) */}
              {store?.address && (
                <a
                  href={store.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([store.address, store.building].filter(Boolean).join(' '))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-1 font-body text-xs text-text-tertiary mt-1 tap-effect underline decoration-dotted underline-offset-2"
                >
                  <MapPin size={11} className="mt-0.5 shrink-0" />
                  <span className="truncate">{store.address}</span>
                </a>
              )}

              {/* #1043: 予約日時超過でユーザー自身はキャンセルできない状態 (= 店舗へ直接連絡が必要)
                  になったら、キャンセル押下時のトーストとは別に電話番号を常時表示する。 */}
              {(r.status === 'approved' || (r.status === 'pending' && r.pendingChange)) && !canCancelApproved(r) && store?.phoneNumber && (
                <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2">
                  <p className="font-body text-[11px] text-amber-900 leading-relaxed">
                    {t('myReservations.cancelDeadlinePassed')}
                  </p>
                  <a
                    href={`tel:${store.phoneNumber}`}
                    className="mt-1 inline-flex items-center gap-1 font-body text-xs font-semibold text-amber-900 tap-effect"
                  >
                    <Phone size={12} />
                    {store.phoneNumber}
                  </a>
                </div>
              )}

              {r.notes && (
                <p className="font-body text-xs text-text-secondary mt-2 bg-gray-50 rounded p-2 whitespace-pre-wrap">
                  {r.notes}
                </p>
              )}

              {/* 承認済み予約への変更の再承認待ち: 却下されても元の予約は残ることを先に伝える */}
              {r.status === 'pending' && r.pendingChange && (
                <p className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 font-body text-[11px] text-amber-900 leading-relaxed">
                  {t('myReservations.pendingChangeNotice', { original: String(r.pendingChange.from) })}
                </p>
              )}

              {/* 店舗が変更を却下した (元の予約のまま) / 店舗が日時を動かした — 直近の履歴で知らせる。
                  店舗からのメッセージ (「この日なら空いています」等) があれば電話への導線と一緒に出す */}
              {(() => {
                const last = r.changeHistory?.[r.changeHistory.length - 1]
                if (!last || (r.status !== 'approved' && r.status !== 'pending')) return null
                const byStoreDatetime = last.type === 'datetime' && last.by === 'store'
                // #1994 'changeExpired' = 店舗が期限内に返答せず、変更を取り下げて元に戻した。
                // 却下と結果は同じだが、店が断ったわけではないので文言を分ける
                const reverted = last.type === 'changeRejected' || last.type === 'changeExpired'
                if (!reverted && !byStoreDatetime) return null
                return (
                  <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 font-body text-[11px] text-amber-900 leading-relaxed">
                    <p>
                      {last.type === 'changeRejected'
                        ? t('myReservations.changeRejectedNotice', { requested: String(last.from), original: String(last.to) })
                        : last.type === 'changeExpired'
                          ? t('myReservations.changeExpiredNotice', { requested: String(last.from), original: String(last.to) })
                          : t('myReservations.datetimeChangedByStore', {
                              from: String(last.from).replace('T', ' '),
                              to: String(last.to).replace('T', ' '),
                            })}
                    </p>
                    {(last.type === 'changeRejected' ? r.storeMessage : last.type === 'changeExpired' ? null : last.reason) && (
                      <p className="mt-1 whitespace-pre-wrap">
                        <span className="font-semibold">{t('myReservations.storeMessageLabel')}: </span>
                        {last.type === 'changeRejected' ? r.storeMessage : last.reason}
                      </p>
                    )}
                    {store?.phoneNumber && (
                      <a href={`tel:${store.phoneNumber}`} className="mt-1 inline-flex items-center gap-1 font-semibold tap-effect">
                        <Phone size={12} />
                        {store.phoneNumber}
                      </a>
                    )}
                  </div>
                )
              })()}

              {/* #1373 期限切れ (店舗が 48h 以内に回答しなかった) の自動キャンセル。
                * お客様には「店が応答しなかった」ことが分からないと、自分の予約が
                * なぜ消えたのか説明がつかない。お金は動いていないことも併せて出す。 */}
              {r.status === 'expired' && (
                <div className="mt-2 rounded bg-orange-50 border border-orange-200 p-2 text-[11px] font-body text-orange-900 leading-relaxed">
                  <p className="font-semibold">{t('myReservations.expiredAutoCancelled')}</p>
                  <p className="mt-0.5">{t('myReservations.expiredReason')}</p>
                  {r.cancelledAt && (
                    <p className="mt-0.5 text-orange-800">
                      {new Date(r.cancelledAt).toLocaleString('ja-JP')}
                    </p>
                  )}
                </div>
              )}

              {/* キャンセル/拒否 詳細 (= 顧客側履歴で「いつ・誰が・なぜ」を見える化) */}
              {(r.status === 'cancelled' || r.status === 'rejected') && (
                <div className="mt-2 rounded bg-gray-50 p-2 text-[11px] font-body text-text-secondary leading-relaxed">
                  {r.cancelledBy && (
                    <p>
                      {t(`myReservations.cancelledBy.${r.cancelledBy}`)}
                    </p>
                  )}
                  {r.cancelledAt && (
                    <p className="mt-0.5 text-text-tertiary">
                      {new Date(r.cancelledAt).toLocaleString('ja-JP')}
                    </p>
                  )}
                  {r.reasonCode && (
                    <p className="mt-0.5 text-text-tertiary">
                      {t('myReservations.cancelReasonLabel', {
                        reason: t(`myReservations.cancelReason.${r.reasonCode}`, { defaultValue: r.reasonCode }),
                      })}
                    </p>
                  )}
                  {/* #1373 誰の都合かで返金額が変わるのに、履歴には理由しか出ていな
                    * かった。お店都合なら手続き料を引かず全額、自己都合なら規定額。
                    * 「全額返ってきていない」という問い合わせはここが見えれば減る。 */}
                  {refundNote(r) && (
                    <p className="mt-1 text-text-primary">{refundNote(r)}</p>
                  )}
                  {/* #2226 カードに返せなかった返金の引換券 (お店で見せる QR。本人だけ) */}
                  {refundCardUnreachable(r) && !r.refundDisputed && <RefundVoucherCard reservationId={r.id} />}
                  {/* 却下時に店舗が残したメッセージ (代替日の提案など) */}
                  {r.status === 'rejected' && r.storeMessage && (
                    <p className="mt-1 whitespace-pre-wrap text-text-primary">
                      <span className="font-semibold">{t('myReservations.storeMessageLabel')}: </span>
                      {r.storeMessage}
                    </p>
                  )}
                </div>
              )}

              {/* #746: 共有された予約と同じ詳細ページへの導線。shortCode 未付与の
                  旧予約は click 時に lazy backfill されるので常時表示で OK。 */}
              <div className="mt-3 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => goToDetail(r)}
                  disabled={resolvingShortCode === r.id}
                  className="flex items-center gap-1 text-xs font-body font-semibold text-navy tap-effect disabled:opacity-50"
                >
                  {t('myReservations.viewDetail')}
                  <ExternalLink size={11} />
                </button>
                {/* #1291: 実際の登録店舗ページ (コース内容) へ遷移 */}
                <button
                  onClick={() => navigate(`/stores/${r.storeId}`)}
                  className="flex items-center gap-1 text-xs font-body font-semibold text-navy tap-effect"
                >
                  <Store size={12} />
                  {t('myReservations.viewStore')}
                </button>
              </div>

              {/* #1374 キャンセル料が「かかるのか / いくらか」を押す前に出す。
                * 承認待ちの間は無料なので、迷っているなら早く決めた方が得だと
                * 分かる。承認後は規定の料金がかかるので、金額まで出す。 */}
              {tab === 'active' && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => openCancelModal(r)}
                    disabled={cancelling === r.id}
                    className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-body font-semibold tap-effect disabled:opacity-50 ${
                      r.status === 'pending'
                        ? 'border-gray-200 text-text-secondary'
                        : 'border-yokoso-red/40 text-yokoso-red'
                    }`}
                  >
                    <X size={12} />
                    {t('myReservations.cancel')}
                  </button>
                  <span className={`font-body text-[11px] leading-snug ${
                    r.status === 'pending' ? 'text-matcha' : 'text-text-secondary'
                  }`}>
                    {cancelFeeNote(r)}
                  </span>
                </div>
              )}

              {tab === 'active' && (
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  {/* #745 Phase 2.1/2.2/2.3: 予約変更系 (active のみ、approved/pending 共通)
                    * #2014 お金を預かっている予約で、店が前回の変更を確認している間は出さない
                    * (サーバも 409 で断る)。変更の「戻す材料」は増分の決済を 1 本しか持てず、
                    * 2 回目の増分が宙に浮くため。当日払い (決済なし) は止めない (backend の
                    * _holds_payment と同じ判定) */}
                  {r.pendingChange && (r.paymentIntentId || isPointsMethod(r.paymentMethod)) ? (
                    <p className="w-full text-right font-body text-[11px] leading-snug text-text-secondary">
                      {t('myReservations.changeLockedPending')}
                    </p>
                  ) : (
                    <>
                      <button
                        onClick={() => setPartySizeTarget(r)}
                        className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-body font-semibold text-navy tap-effect"
                      >
                        <Users size={12} />
                        {t('myReservations.changeParty')}
                      </button>
                      <button
                        onClick={() => setDatetimeTarget(r)}
                        className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-body font-semibold text-navy tap-effect"
                      >
                        <Calendar size={12} />
                        {t('myReservations.changeDatetime')}
                      </button>
                      {/* コース変更は course 予約のみ表示 */}
                      {r.courseId && (
                        <button
                          onClick={() => setCourseTarget(r)}
                          className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-body font-semibold text-navy tap-effect"
                        >
                          <UtensilsCrossed size={12} />
                          {t('myReservations.changeCourse')}
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => openShareSheet(r)}
                    disabled={resolvingShortCode === r.id}
                    className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-body font-semibold text-navy tap-effect disabled:opacity-50"
                  >
                    <Share2 size={12} />
                    {t('myReservations.share')}
                  </button>
                </div>
              )}
            </div>
            )
          })}
          {/* #1291: 体験チケット (Pattern 2) — 飲食店予約と別デザイン。基本情報は
             コース予約に寄せ (店名/コース名/日時/人数/料金)、購入後は変更不可なので
             変更系アクションは無し (店舗ページ / 共有 / 追加購入 のみ)。 */}
          {ticketGroups.map((g) => {
            const store = storeById(g.storeId)
            // #1292: 1 チケット = quantity 名分。人数は quantity を合算。
            const count = g.tickets.reduce((s, ti) => s + (ti.quantity || 1), 0)
            const total = g.priceJpy * count
            const name = locale === 'ja' ? g.productName : g.productNameEn || g.productName
            return (
            <div key={g.key} className="bg-white rounded-2xl shadow-card p-4">
              {/* 店名 (コース予約カードと同じヘッダ) */}
              <p className="font-jp font-semibold text-text-primary truncate mb-2">
                {storeNameById(g.storeId)}
              </p>
              {/* コース名 + 基本情報 (日時: フリー / 人数 / 料金) */}
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                  <Ticket size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-semibold text-text-primary">{name}</p>
                  <div className="mt-1 space-y-0.5 font-body text-xs text-text-secondary">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={12} className="shrink-0 text-text-tertiary" />
                      {/* #1292: timed は「8/13(火) 15:00の回」、free_entry は「時間指定なし」 */}
                      {g.slotDate
                        ? t('experience.slotOfDay', { datetime: formatSlotDateTime(g.slotDate, g.slotStartTime, locale) })
                        : t('experience.scheduleFree')}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users size={12} className="shrink-0 text-text-tertiary" />
                      {t('publicReservation.partySize', { count: String(count) })}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Ticket size={12} className="shrink-0 text-text-tertiary" />
                      ¥{g.priceJpy.toLocaleString()} × {count} = ¥{total.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* 店舗住所 (コース予約と同じ地図リンク) */}
              {store?.address && (
                <a
                  href={store.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([store.address, store.building].filter(Boolean).join(' '))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-1 font-body text-xs text-text-tertiary mt-2 tap-effect underline decoration-dotted underline-offset-2"
                >
                  <MapPin size={11} className="mt-0.5 shrink-0" />
                  <span className="truncate">{store.address}</span>
                </a>
              )}

              {/* QR チケット (枚数分) — タップで各チケットの QR へ */}
              <div className="mt-3 flex flex-wrap gap-2">
                {g.tickets.map((ti, i) => (
                  <button
                    key={ti.id}
                    onClick={() => navigate(`/my-tickets/${ti.id}`)}
                    className="tap-effect flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-body text-text-primary"
                  >
                    <Ticket size={12} className="text-gold" />
                    {t('experience.ticketNth', { n: i + 1 })}
                    {ticketBadge(ti)}
                  </button>
                ))}
              </div>

              {/* アクション: 購入後変更不可のため変更系なし。店舗ページ / 共有 / 追加購入 */}
              <div className="mt-3 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => navigate(`/stores/${g.storeId}`)}
                  className="flex items-center gap-1 text-xs font-body font-semibold text-navy tap-effect"
                >
                  <Store size={12} />
                  {t('myReservations.viewStore')}
                </button>
                {/* #1291: 共有はコース予約と同じ ReservationShareSheet を使う。
                    共有先は予約内容の公開ページ (/e/:shareCode) */}
                <button
                  onClick={() => openTicketShare(g)}
                  disabled={resolvingTicketShare === g.key}
                  className="flex items-center gap-1 text-xs font-body font-semibold text-navy tap-effect disabled:opacity-50"
                >
                  <Share2 size={12} />
                  {t('myReservations.share')}
                </button>
                {g.productId && (
                  <button
                    onClick={() => navigate(`/experience/buy/${g.productId}`)}
                    className="flex items-center gap-1 text-xs font-body font-semibold text-yokoso-red tap-effect"
                  >
                    <Ticket size={12} />
                    {t('experience.spotBuyMore')}
                  </button>
                )}
              </div>
            </div>
            )
          })}
          </>
        )}
      </div>

      {/* Cancel reason modal */}
      {cancelTarget && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-text-primary">
                {t('myReservations.cancelReasonTitle')}
              </h3>
              <button
                onClick={() => setCancelTarget(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
              >
                <X size={16} className="text-text-secondary" />
              </button>
            </div>
            <div className="space-y-2 mb-5">
              {CANCEL_REASONS.map(code => (
                <button
                  key={code}
                  onClick={() => setCancelReason(code)}
                  className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm font-body transition-colors ${
                    cancelReason === code
                      ? 'border-navy bg-navy/10 text-navy font-semibold'
                      : 'border-gray-200 bg-white text-text-primary'
                  }`}
                >
                  {t(`myReservations.cancelReason.${code}`)}
                </button>
              ))}
            </div>
            {/* #2021 確定ボタンの直前で、いくら返り・いくら引かれるかを出して確認を取る。
              * 一覧の横の小さな文言だけでは、押す時点で金額を見ていない */}
            <div className="mb-5 rounded-lg border border-gold/50 bg-gold/10 p-3">
              <p className="font-body text-xs font-semibold text-text-primary mb-1">
                {t('myReservations.cancelFeeConfirmHeading')}
              </p>
              <p className="font-body text-sm leading-snug text-text-primary">
                {cancelFeeNote(cancelTarget)}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setCancelTarget(null)}
                className="flex-1 h-11 rounded-xl border border-gray-200 text-sm font-body font-semibold text-text-secondary"
              >
                {t('myReservations.close')}
              </button>
              <button
                onClick={submitCancel}
                disabled={!!cancelling}
                className={`flex-1 h-11 rounded-xl text-sm font-body font-semibold transition-colors ${
                  cancelling ? 'bg-gray-300 text-white' : 'bg-yokoso-red text-white tap-effect'
                }`}
              >
                {t('myReservations.cancelAction')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* #746: 予約共有 sheet */}
      {shareTarget && (
        <ReservationShareSheet
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          reservation={{
            storeName: storeNameById(shareTarget.storeId),
            storeAddress: storeById(shareTarget.storeId)?.address,
            date: shareTarget.date,
            time: shareTarget.time,
            partySize: shareTarget.partySize,
            courseName: shareTarget.courseName,
            paymentTiming: shareTarget.paymentTiming,
            shortCode: shareTarget.shortCode,
          }}
        />
      )}

      {/* #1291: 体験チケットの共有 (予約と同じ sheet を流用)。フリー日時 +
          予約内容の公開ページ URL (/e/:shareCode) → 友達が「何を予約したか」を見られる。 */}
      {ticketShareTarget && (
        <ReservationShareSheet
          open={!!ticketShareTarget}
          onClose={() => setTicketShareTarget(null)}
          shareUrl={`${window.location.origin}/e/${ticketShareTarget.shareCode}`}
          scheduleLabel={
            ticketShareTarget.group.slotDate
              ? formatSlotDateTime(ticketShareTarget.group.slotDate, ticketShareTarget.group.slotStartTime, locale)
              : t('experience.scheduleFree')
          }
          reservation={{
            storeName: storeNameById(ticketShareTarget.group.storeId),
            storeAddress: storeById(ticketShareTarget.group.storeId)?.address,
            date: '',
            time: '',
            partySize: ticketShareTarget.group.tickets.length,
            courseName: locale === 'ja' ? ticketShareTarget.group.productName : ticketShareTarget.group.productNameEn || ticketShareTarget.group.productName,
            paymentTiming: undefined,
            shortCode: undefined,
          }}
        />
      )}

      {/* #745 Phase 2.1: 人数変更モーダル */}
      {partySizeTarget && (
        <ChangePartySizeModal
          open={!!partySizeTarget}
          onClose={() => setPartySizeTarget(null)}
          reservation={partySizeTarget}
          storeName={storeNameById(partySizeTarget.storeId)}
          pricePerPerson={
            // #1644 サーバの再計算 (コース + 飲み放題の 1 人単価) と同じ式 —
            // コース定義だけだとプラン付き予約で増額・返金プレビューが実額とズレる
            targetCourse
              ? targetCourse.priceYen + (partySizeTarget.drinkPlanPriceYen ?? 0)
              : undefined
          }
          minPartySize={targetCourse?.partySizeRange.min ?? 1}
          maxPartySize={targetCourse?.partySizeRange.max ?? DEFAULT_MAX_PARTY_SIZE}
        />
      )}

      {/* #745 Phase 2.2: 日時変更モーダル */}
      {datetimeTarget && (
        <ChangeDatetimeModal
          open={!!datetimeTarget}
          onClose={() => setDatetimeTarget(null)}
          reservation={datetimeTarget}
          storeName={storeNameById(datetimeTarget.storeId)}
        />
      )}

      {/* #745 Phase 2.3: コース変更モーダル */}
      {courseTarget && (
        <ChangeCourseModal
          open={!!courseTarget}
          onClose={() => setCourseTarget(null)}
          reservation={courseTarget}
          storeName={storeNameById(courseTarget.storeId)}
        />
      )}
    </div>
  )
}
